import CodeEditor from "components/CodeEditor";
import PolicyTypeBadge from "components/PolicyTypeBadge";
import { External } from "components/icons/generated";
import Box from "ds/components/Box";
import LinkButtonIcon from "ds/components/ButtonIcon/LinkButtonIcon";
import CardCollapsible from "ds/components/Card/Collapsible";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import Tag from "ds/components/Tag";
import { useToggle } from "hooks/useToggle";
import useTypedContext from "hooks/useTypedContext";
import useDetachPolicy from "shared/Policy/useDetachPolicy";
import { StackPolicyAttachment } from "types/generated";
import { getAttachedLabel } from "utils/labels";
import PermissionGateDropdownItem from "components/PermissionGate/DropdownMenuItem";

import { ModuleContext } from "../Context";
import { showModulePoliciesDetachConfirmationModal } from "./ConfirmationModal";
import { GET_POLICIES_REFETCH_QUERIES } from "./constants";
import styles from "./styles.module.css";

type ModulePoliciesListItemProps = {
  policy: StackPolicyAttachment;
  moduleLabels?: string[];
};

const ModulePoliciesListItem = ({ policy, moduleLabels }: ModulePoliciesListItemProps) => {
  const isAutoAttached = policy.isAutoattached;
  const policyLink = `/policy/${policy.policyId}/`;
  const [isCollapsed, toggle] = useToggle(true);

  const { canManageModule } = useTypedContext(ModuleContext);

  const { detachPolicy, loading } = useDetachPolicy(GET_POLICIES_REFETCH_QUERIES);

  const onDelete = () => {
    detachPolicy(policy.id);
  };

  const onDetachConfirm = () => {
    showModulePoliciesDetachConfirmationModal({
      onConfirm: onDelete,
      name: policy.policyName,
    });
  };

  return (
    <CardCollapsible
      ariaLevel={4}
      ariaLabel={policy.policyName}
      title={policy.policyName}
      size="small"
      isCollapsed={isCollapsed}
      onToggle={toggle}
      titleSuffix={
        <LinkButtonIcon icon={External} to={policyLink} variant="ghost">
          Go to policy editor
        </LinkButtonIcon>
      }
      headerSuffix={
        <Box align="center" gap="medium" shrink="0">
          {/* FYI: show only the first auto attached label from the list */}
          {isAutoAttached && moduleLabels && (
            <Tag tag={getAttachedLabel(moduleLabels, policy.policyLabels)} withAutoAttach />
          )}

          <PolicyTypeBadge type={policy.policyType} />

          <DropdownMenuEllipsis tooltip="Policy actions" buttonVariant="ghost">
            <DropdownMenuItem href={policyLink}>Go to policy editor</DropdownMenuItem>
            {!isAutoAttached && (
              <PermissionGateDropdownItem
                access={canManageModule}
                loading={loading}
                danger
                onAction={onDetachConfirm}
              >
                Detach policy
              </PermissionGateDropdownItem>
            )}
          </DropdownMenuEllipsis>
        </Box>
      }
    >
      <CodeEditor
        className={styles.codeEditor}
        body={policy.policyBody}
        language="rego"
        autoHeight
        maxAutoHeight={460}
        readOnly
      />
    </CardCollapsible>
  );
};

export default ModulePoliciesListItem;
